import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { HandHeart, Users, Target, ArrowUpRight } from "lucide-react";
import { API, formatRupiah, formatNumber, donateEvent, scrollToId } from "@/lib/site";
import { Reveal, Tilt } from "./motion-prims";

function Progress({ collected, target }) {
  const pct = Math.min(100, Math.round((collected / target) * 100));
  return (
    <div>
      <div className="flex items-end justify-between mb-2">
        <p className="font-display font-black text-xl sm:text-2xl text-olive">{formatRupiah(collected)}</p>
        <p className="text-sm font-bold text-gold-dark">{pct}%</p>
      </div>
      <div className="h-3.5 rounded-full border-2 border-ink bg-cream overflow-hidden">
        <div
          data-testid={`program-progress-${pct}`}
          className="h-full bg-gold transition-[width] duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function CampaignCard({ c, wide }) {
  return (
    <Tilt max={5} className={`h-full ${wide ? "lg:col-span-2" : ""}`}>
      <article
        data-testid={`program-card-${c.id}`}
        className="group h-full bg-cream border-2 border-ink rounded-2xl overflow-hidden shadow-retro transition-shadow hover:shadow-retro-lg flex flex-col"
      >
        <div className={`relative overflow-hidden ${wide ? "sm:h-64" : "h-48 sm:h-56"}`}>
          <img
            src={c.image}
            alt={c.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span
            className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.15em] border-2 border-ink rounded-full px-3 py-1.5 ${
              c.status === "Urgent" ? "bg-terra text-cream" : "bg-cream text-ink"
            }`}
          >
            {c.status}
          </span>
          <span className="absolute bottom-3 left-3 text-[10px] font-bold uppercase tracking-[0.15em] bg-ink text-gold-soft rounded-full px-3 py-1.5">
            {c.category}
          </span>
        </div>
        <div className="p-5 sm:p-6 flex flex-col gap-4 flex-1">
          <h3 className="font-display font-black text-xl sm:text-2xl tracking-tight">{c.title}</h3>
          <p className="text-sm sm:text-base leading-relaxed text-pine flex-1">{c.description}</p>
          <Progress collected={c.collected} target={c.target} />
          <div className="flex items-center justify-between text-xs sm:text-sm text-pine font-semibold">
            <span className="flex items-center gap-1.5">
              <Target className="w-4 h-4 text-gold-dark" /> Target {formatRupiah(c.target)}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="w-4 h-4 text-gold-dark" /> {formatNumber(c.donors)} donatur
            </span>
          </div>
          <button
            data-testid={`program-donate-${c.id}`}
            onClick={() => {
              donateEvent(c.id);
              scrollToId("donasi");
            }}
            className="btn-retro bg-gold text-ink rounded-full px-6 py-3 text-sm w-full"
          >
            <HandHeart className="w-4 h-4" /> Donasi Program Ini
          </button>
        </div>
      </article>
    </Tilt>
  );
}

export default function Programs() {
  const { data: campaigns, isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => (await axios.get(`${API}/campaigns`)).data,
  });

  return (
    <section id="program" className="scroll-mt-20 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-gold-dark">Pilih Program Kerja</p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display font-black tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
              Setiap rupiah pulang ke <span className="italic text-gold-dark">kebutuhan nyata.</span>
            </h2>
            <p className="max-w-sm text-sm sm:text-base text-pine leading-relaxed">
              Program kerja utama Public Relations 2025 yang sedang berjalan. Mulai dari Rp 10.000.
            </p>
          </div>
        </Reveal>

        <div className="mt-10 sm:mt-14 grid gap-6 sm:gap-8 lg:grid-cols-2">
          {isLoading && <p className="text-pine font-semibold">Memuat program…</p>}
          {(campaigns || []).map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08} className={`h-full ${i === 0 ? "lg:col-span-2" : ""}`}>
              <CampaignCard c={c} wide={i === 0} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-10 flex justify-center">
          <button
            data-testid="program-all-donate"
            onClick={() => scrollToId("donasi")}
            className="inline-flex items-center gap-2 font-bold text-olive hover:text-gold-dark transition-colors"
          >
            Tidak sempat memilih? Donasi umum saja <ArrowUpRight className="w-4 h-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
